import {  useEffect, useRef, useState, useMemo } from 'react'
import { withStyles } from '@aura/components/styles'
import {  Box, Button, Container, Grid, Typography } from '@aura/components'
import { useAuraThemeSwitcher } from '@aura/components/themes'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import { useTranslation } from 'react-i18next'

import PropTypes from 'prop-types'
import CustomLoader from '../../../../components/custom-loader'
import colors from '../../../../../public/colors'
import RegistrationContainer from '../../../../components/new-registration-container'
import SubmitVerifyAuthenticatorCodeTypes from '../../../../constants/verify-authenticator-code'
import sanitize from '../../../../utils/sanitize'
import styles from './styles'


const VerificationCodeInput = ({
    value,
    onChange,
    length,
    classes,
    theme,
    translate,
    isVerified,
    verificationError
}) => {
    const [ isFocusedOnce, setIsFocusedOnce ] = useState(false)
    const refs = useRef([ ...Array(length) ].map(() => null))

    useEffect(() => {
        if (verificationError && value.length > 0) {
            const lastFilledIndex = value.length - 1
            setTimeout(() => {
                if (refs.current[lastFilledIndex]) {
                    refs.current[lastFilledIndex].focus()
                }
            }, 100)
        }
    }, [ verificationError ])

    const handleChange = (idx, e) => {
        const val = e.target.value.replace(/[^0-9]/g, '')
        let arr = value.split('')
        arr[idx] = val ? val[0] : ''
        let newVal = arr.join('')
        onChange(newVal.length > length ? newVal.slice(0, length) : newVal)
        val && idx < length - 1
            ? setTimeout(() => refs.current[idx + 1].focus(), 0)
            : null
        !isFocusedOnce && idx !== 0
            ? setIsFocusedOnce(true)
            : null
    }

    const handleKeyDown = (idx, e) => {
        e.key === 'Backspace' && !value[idx] && idx > 0
            ? refs.current[idx - 1].focus()
            : null
        !isFocusedOnce && idx !== 0
            ? setIsFocusedOnce(true)
            : null
    }

    return (
        <Box className={`${classes.verificationCodeBox} ${classes.verificationContainerSMS}`}>
            <Typography
                className={classes.verificationCodeLabel}
                style={{
                    color: isFocusedOnce && value.length === 0
                        ? colors[theme.palette.mode].errorText
                        : ''
                }}
            >
                {translate('registrationFlow.verificationCode')}
            </Typography>
            <Box className={classes.verificationCodeInputContainer}>
                {[ ...Array(length) ].map((_, idx) => (
                    <input
                        key={idx}
                        ref={el => refs.current[idx] = el}
                        type='text'
                        inputMode='numeric'
                        maxLength={1}
                        autoFocus={idx === 0}
                        value={value[idx] || ''}
                        onChange={e => handleChange(idx, e)}
                        onKeyDown={e => handleKeyDown(idx, e)}
                        disabled={isVerified || (idx > 0 ? !value[idx - 1] : false)}
                        className={classes.verificationCodeInput}
                        style={{
                            borderBottom: isVerified
                                ? `2px solid ${colors[theme.palette.mode].successTextLight}`
                                : verificationError ? `2px solid ${colors[theme.palette.mode].errorText}`
                                    : value[idx]
                                        ? `2px solid ${colors[theme.palette.mode].verificationCodeInputFilledBorder}`
                                        : `2px solid ${colors[theme.palette.mode].verificationCodeInputUnfilledBorder}`,
                            color: isVerified ? colors[theme.palette.mode].successText : '',
                            pointerEvents: isVerified ? 'none' : 'auto'
                        }}
                    />
                ))}
            </Box>
        </Box>
    )
}

const Component = ({
    classes,
    isProcessing,
    onBack,
    onNext,
    onSuccess,
    title,
    status,
    verbiage
}) => {
    const isVerified = status === SubmitVerifyAuthenticatorCodeTypes.SUCCESS
    const error = status === SubmitVerifyAuthenticatorCodeTypes.ERROR
    const { theme } = useAuraThemeSwitcher()

    const [ passCode, setPassCode ] = useState('')

    const { t: translate, i18n } = useTranslation()
    useEffect(() => {
        if( isVerified ){
            setTimeout(() => {
                onSuccess()
            }, 3000)
            
        }
    }, [ isVerified ])

    const footerContent = useMemo(() => {
        const footerButtons = [
            {
                className: classes.backButton,
                disabled: false,
                label: translate('back'),
                onClick: onBack,
                variant: 'text'
            },
            {
                className: `${classes.nextButton} ${passCode.length !== 6   ? classes.nextButtonDisabled : ''}`,
                disabled: passCode.length !== 6,
                label: translate('next'),
                onClick: ()=>{
                    onNext(passCode)
                }
            }
        ]

        return (
            <Grid className={classes.buttonContainer}>
                {footerButtons.map((button, index) => (
                    <Button
                        key={`footer-button-${index}`}
                        className={button.className}
                        disabled={button.disabled}
                        onClick={button.onClick}
                        variant={button.variant}
                    >
                        {button.label}
                    </Button>
                ))}
            </Grid>
        )
    }, [ classes.buttonContainer, classes.cancelButton, classes.nextButton, classes.nextButtonDisabled, translate, onBack, onNext, passCode, status ])

    const mainContent = isProcessing ? <Container className={classes.customLoader}>
        <CustomLoader
            label={translate('loading')}
        />
    </Container> : <>
        <Box className={classes.content}>
            {
                <Typography className={classes.subTitle}>{translate(`authenticators.${verbiage}`)}</Typography>
            }

            <Grid className={classes.verificationContainer}>
                <VerificationCodeInput
                    value={passCode}
                    onChange={val => setPassCode(val)}
                    length={6}
                    classes={classes}
                    theme={theme}
                    translate={translate}
                    isVerified={isVerified}
                    verificationError={error}
                />
                {isVerified && (
                    <Grid className={classes.verificationSuccess}>
                        <CheckCircleOutlineIcon className={classes.verificationSuccessIcon} />
                        <Typography className={classes.verificationSuccessText}>
                            {translate('registrationFlow.verificationSuccess')}
                        </Typography>

                    </Grid>
                )}
                {error && (
                    <Box className={classes.verificationStatusContainer}>
                        {error && (
                            <Box className={classes.verificationError}>
                                <ErrorOutlineOutlinedIcon className={classes.verificationErrorIcon} />
                                <Typography className={classes.verificationErrorText}>
                                    {translate('registrationFlow.incorrectVerificationCode')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

            </Grid>

        </Box>
        <div className={`${classes.helpText} ${isVerified ? classes.helpTextVerified : ''}`}>
            <p className={classes.subTitle}>{translate('registrationFlow.helpText')}</p>
        </div>
    </>

    const stepVerbiage = <div dangerouslySetInnerHTML={{ __html: sanitize(translate('registrationFlow.verbiage', { verbiage: verbiage })) }} />

    return (
        <RegistrationContainer {...{ footerActions : !isVerified && !isProcessing && footerContent, mainContent,  title: translate(`authenticatorOptions.${title}`), verbiage: stepVerbiage }}>
        </RegistrationContainer>
    )
}

Component.displayName = 'AuthenticatorCode'

Component.propTypes = {
    classes: PropTypes.shape({
        button: PropTypes.object,
        loader: PropTypes.object,
        root: PropTypes.object
    }),
    data: PropTypes.object,
    error: PropTypes.object,
    onBack: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    title: PropTypes.string,
    verbiage: PropTypes.string
}

export default withStyles(Component, styles)
